import { FocusMonitor } from '@angular/cdk/a11y';
import { CdkDrag, CdkDragHandle, DragRef, Point } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { ChatStore, PromptGeneratorService } from '@coin/chat-bot-data-access';
import { ChatDialogFooterComponent, ChatDialogHeaderComponent, ChatStartViewComponent } from '@coin/chat-bot-ui';
import { CocoCelebrationService } from '@coin/chat-bot-util-types';
import { GetImageWithGidService } from '@coin/shared/auth/data-access';
import { CopilotChat } from '@copilotkitnext/angular';
import { TranslateModule } from '@ngx-translate/core';
import { distinctUntilChanged, filter, fromEvent, pairwise, Subject, switchMap, takeUntil, timer } from 'rxjs';
import { ResizableDirective } from '../../directives/resizable/resizable.directive';
import { CelebrationAnimationComponent } from '../celebration-animation/celebration-animation.component';
import { ChatFaqScreenComponent } from '../chat-faq-screen/chat-faq-screen.component';

// Minimal component that renders nothing — suppresses CopilotKit's default input UI
@Component({ template: '', standalone: true })
class NullInputComponent {}

@Component({
  selector: 'coin-chat-dialog',
  templateUrl: './chat-dialog.component.html',
  styleUrl: './chat-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ChatDialogHeaderComponent,
    ChatDialogFooterComponent,
    ChatStartViewComponent,
    TranslateModule,
    ChatFaqScreenComponent,
    CdkDrag,
    CdkDragHandle,
    ResizableDirective,
    MatIcon,
    CelebrationAnimationComponent,
    CopilotChat
  ],
  host: {
    '[class.agent-error]': 'hasAgentError()',
    '(keydown)': 'handleKeydown($event)',
    '(click)': 'openProseLinksInNewTab($event)'
  }
})
export class ChatDialogComponent {
  private readonly chatBox = viewChild<ElementRef<HTMLDivElement>>('chatBox');
  private readonly copilotChatRef = viewChild<CopilotChat>('copilotChat');
  protected readonly chatStore = inject(ChatStore);
  protected readonly promptGenerator = inject(PromptGeneratorService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly focusMonitor = inject(FocusMonitor);
  private readonly elRef = inject(ElementRef);
  private readonly imageService = inject(GetImageWithGidService);
  private readonly cocoCelebrationService = inject(CocoCelebrationService);

  readonly nullInputType = NullInputComponent;

  isActive = true;
  readonly isFaqScreenActive = signal(false);

  readonly hasNoMessages = computed(() => (this.copilotChatRef()?.agentStore()?.messages() ?? []).length === 0);
  readonly hasMessages = computed(() => !this.hasNoMessages());
  readonly isAgentRunning = computed(() => this.copilotChatRef()?.agentStore()?.isRunning() ?? false);

  /** True from submitMessage() until the agent run completes (covers the pre-RUN_STARTED gap). */
  private readonly _isSubmitting = signal(false);

  /** True while the agent is busy (submitting OR running). Covers the full lifecycle. */
  protected readonly isAgentBusy = computed(() => this._isSubmitting() || this.isAgentRunning());

  /** True for the entire agent run — from submission until RUN_FINISHED. */
  protected readonly isThinking = this.isAgentBusy;

  private readonly _agentError = signal(false);
  private readonly _lastSubmittedMessage = signal('');
  private readonly cancelErrorCheck$ = new Subject<void>();
  protected readonly hasAgentError = computed(() => !this.isAgentRunning() && this._agentError());

  protected readonly celebrationType = signal<string | null>(this.cocoCelebrationService.getType());
  protected readonly showHoliBackground = computed(() => this.celebrationType() === 'holi');

  constructor() {
    // Set COCO avatar CSS variable
    const cocoAvatarPath = this.cocoCelebrationService.getAvatar();
    if (cocoAvatarPath) {
      this.elRef.nativeElement.style.setProperty('--coco-avatar-url', `url('${cocoAvatarPath}')`);
    }

    this.loadUserProfilePicture();
    this.detectAgentError();
    this.hideEmptyAssistantMessages();

    // Monitor focus
    this.focusMonitor
      .monitor(this.elRef.nativeElement, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(origin => {
        this.isActive = !!origin;
      });

    this.monitorChatBoxClick();
  }

  /** Load user profile picture and set CSS variable. */
  private loadUserProfilePicture(): void {
    effect(cleanup => {
      const gid = this.chatStore.user()?.gid;
      if (gid) {
        const sub = this.imageService
          .getImageWithStore(gid)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(img => {
            if (img) {
              this.elRef.nativeElement.style.setProperty('--user-avatar-url', `url('${img}')`);
            }
          });
        cleanup(() => sub.unsubscribe());
      }
    });
  }

  /** Detect agent error: agent ran but stopped without producing assistant content. */
  private detectAgentError(): void {
    const isRunning$ = toObservable(this.isAgentRunning);

    // Clear error when agent starts running
    isRunning$.pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef)).subscribe(() => this._agentError.set(false));

    // Detect error: agent stopped without producing assistant content
    isRunning$
      .pipe(
        distinctUntilChanged(),
        pairwise(),
        filter(([wasRunning, isRunning]) => wasRunning && !isRunning),
        switchMap(() => timer(150).pipe(takeUntil(this.cancelErrorCheck$))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        const messages = this.copilotChatRef()?.agentStore()?.messages() ?? [];
        const lastMsg = messages.at(-1);
        const hasContent =
          lastMsg?.role === 'assistant' && typeof lastMsg.content === 'string' && lastMsg.content.trim() !== '';
        if (!hasContent) {
          this._agentError.set(true);
        }
      });
  }

  /**
   * Hide empty assistant messages (tool-call / thought steps) by setting a data attribute
   * directly on the <copilot-chat-assistant-message> element.
   */
  private hideEmptyAssistantMessages(): void {
    effect(() => {
      // Subscribe to messages signal so the effect re-runs on every message change
      this.copilotChatRef()?.agentStore()?.messages();
      const isBusy = this.isAgentBusy();

      // Defer to next microtask so CopilotKit has flushed its render
      setTimeout(() => {
        const assistantEls = this.elRef.nativeElement.querySelectorAll(
          'copilot-chat-assistant-message'
        ) as NodeListOf<HTMLElement>;

        assistantEls.forEach((el, idx) => {
          // While agent is busy, protect the last assistant message (currently streaming)
          if (isBusy && idx === assistantEls.length - 1) {
            el.removeAttribute('data-coco-empty');
            return;
          }

          const rendererContent = el.querySelector('copilot-chat-assistant-message-renderer > div');
          const hasContent = rendererContent && rendererContent.innerHTML.trim().length > 0;

          if (hasContent) {
            el.removeAttribute('data-coco-empty');
          } else {
            el.setAttribute('data-coco-empty', 'true');
          }
        });
      }, 0);
    });
  }

  /** Monitor click inside chat box to mark dialog as active. */
  private monitorChatBoxClick(): void {
    toObservable(this.chatBox)
      .pipe(
        filter(Boolean),
        switchMap(chatBox => fromEvent(chatBox.nativeElement, 'click')),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.isActive = true;
      });
  }

  async submitMessage(message: string): Promise<void> {
    this._lastSubmittedMessage.set(message);
    this._agentError.set(false);
    this._isSubmitting.set(true);
    try {
      await this.copilotChatRef()?.submitInput(message);
    } finally {
      this._isSubmitting.set(false);
    }
  }

  clearConversation(): void {
    this._agentError.set(false);
    this.cancelErrorCheck$.next();
    this.chatStore.clearConversation();
  }

  protected async retryLastMessage(): Promise<void> {
    const lastMsg = this._lastSubmittedMessage();
    if (!lastMsg) return;
    this._agentError.set(false);
    this.cancelErrorCheck$.next();
    this.chatStore.clearConversation();
    // Wait for threadId change to propagate so CopilotKit creates a new AgentStore.
    // TODO: Replace with a deterministic signal check once CopilotKit exposes a ready state.
    await new Promise<void>(resolve => setTimeout(resolve, 50));
    await this.submitMessage(lastMsg);
  }

  constrainPosition = (
    userPointerPosition: Point,
    _dragRef: DragRef,
    dimensions: DOMRect,
    pickupPositionInElement: Point
  ) => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate top-left position of the dialog based on cursor offset
    let newX = userPointerPosition.x - pickupPositionInElement.x;
    let newY = userPointerPosition.y - pickupPositionInElement.y;

    // Clamp horizontally
    if (newX < 0) newX = 0;
    else if (newX + dimensions.width > viewportWidth) newX = viewportWidth - dimensions.width;

    // Clamp vertically
    if (newY < 0) newY = 0;
    else if (newY + dimensions.height > viewportHeight) newY = viewportHeight - dimensions.height;

    return { x: newX, y: newY };
  };

  /** Open links inside CopilotKit's rendered markdown in a new tab. */
  protected openProseLinksInNewTab(event: MouseEvent): void {
    const anchor = (event.target as HTMLElement).closest('.prose a');
    if (anchor instanceof HTMLAnchorElement && anchor.href) {
      event.preventDefault();
      window.open(anchor.href, '_blank', 'noopener,noreferrer');
    }
  }

  closeChat() {
    this.chatStore.toggle();
  }

  handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.isActive && this.isChatActive() && this.chatStore.isOpen()) {
      event.preventDefault();
      this.closeChat();
    }
  }

  private isChatActive() {
    const focusedEl = document.activeElement;
    return this.chatBox()?.nativeElement.contains(focusedEl) || false;
  }

  toggleFaq() {
    this.isFaqScreenActive.update(value => !value);
  }
}
